/**
 * Network resilience utilities for Firebase operations
 * Provides retry functionality for network operations with better error handling
 */

/**
 * Executes a Firebase operation with retry functionality
 * @param {Function} operation - The Firebase operation to execute (must return a Promise)
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} options.initialDelay - Initial delay in ms before first retry (default: 1000)
 * @param {number} options.backoffFactor - Factor to increase delay by on each retry (default: 1.5)
 * @param {boolean} options.shouldRetry - Function to determine if retry should happen for error (default: network errors only)
 * @returns {Promise<any>} - Result of the operation
 */
export const withRetry = async (operation, options = {}) => {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    backoffFactor = 1.5,
    shouldRetry = (error) => {
      // Default retry condition: only retry for network/connectivity errors
      return (
        error.code === 'auth/network-request-failed' ||
        error.code === 'firestore/unavailable' ||
        error.code === 'firestore/network-request-failed' ||
        error.message?.includes('network') ||
        error.message?.includes('offline') ||
        error.name === 'NetworkError' ||
        !navigator.onLine
      );
    }
  } = options;

  let lastError;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // For the first attempt, just execute the operation
      if (attempt === 0) {
        return await operation();
      }
      
      // For retries, log the attempt and wait before trying again
      console.log(`🔄 Retry attempt ${attempt}/${maxRetries} after ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Check if we should retry for this error
      if (!shouldRetry(error) || attempt === maxRetries) {
        console.log(`❌ Operation failed after ${attempt} ${attempt === 1 ? 'try' : 'tries'}:`, error);
        throw error;
      }
      
      // Increase delay for next attempt using exponential backoff
      delay = delay * backoffFactor;
    }
  }
  
  // If we somehow get here, throw the last error
  throw lastError;
};

/**
 * Checks if an error is a network-related error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a network error
 */
export const isNetworkError = (error) => {
  if (!error) return false;
  
  return (
    error.code === 'auth/network-request-failed' ||
    error.code === 'firestore/unavailable' ||
    error.code === 'firestore/network-request-failed' ||
    error.message?.includes('network') ||
    error.message?.includes('offline') ||
    error.message?.includes('Failed to fetch') ||
    error.name === 'NetworkError' ||
    !navigator.onLine
  );
};

/**
 * Creates a network-resilient version of a Firebase function
 * @param {Function} firebaseFunction - The original Firebase function
 * @param {Object} options - Retry options
 * @returns {Function} - The wrapped function with retry capability
 */
export const createResilientFirebaseFunction = (firebaseFunction, options = {}) => {
  return (...args) => withRetry(() => firebaseFunction(...args), options);
};
